// チュートリアルの案内役（ユズハ）の吹き出しと、示す所だけを明るく残す覆い。
// say() は台詞を順に出し、全部見終えたら解決する。台詞ごとに：
//   target … 示す所（セレクタ・要素・矩形を返す関数）。無ければ画面の中ほどに吹き出しだけ
//   wait   … 進む条件（true になったら次へ）。指定すると覆いは押せるまま（示した所を実際に操作してもらう）。無ければ画面を押すと次へ
import { esc } from '../ui/dom';

export type Face = 'normal' | 'happy' | 'surprised' | 'point';
export type Target = string | HTMLElement | (() => DOMRect | HTMLElement | null) | null;
export interface Line {
  text: string;
  face?: Face;
  target?: Target;
  wait?: () => boolean;
  /** wait のときに出す小さな案内（例：「賽を押してください」）。無ければ出さない */
  hint?: string;
  /** wait のとき、示す所のほかに触ってよい所（セレクタ・要素を返す関数）。それ以外は押せない */
  allow?: (string | (() => Element | null))[];
  /** target のほかに一緒に光らせる所（触れるかどうかは allow で決める）。例：長押しするメダリオンと、奥義に変わる術のボタン */
  also?: Target[];
  /** 話し手（既定は案内役のユズハ）。shuten＝酒呑童子（プロローグ）・you＝主人公（顔を出さない影） */
  who?: Speaker;
}
export type Speaker = 'yuzuha' | 'shuten' | 'you' | 'system';

export const GUIDE_NAME = 'ユズハ';
/** 話し手の名前と顔（顔は CSS の背景。主人公は影なので絵を出さない） */
const SPEAKERS: Record<Speaker, { name: string; face: () => string; cls: string }> = {
  yuzuha: { name: GUIDE_NAME, face: () => guideFaceStyle(), cls: '' },
  // 酒呑童子は物語の立ち絵（三百年前の姿）の顔のあたり
  shuten: { name: '酒呑童子', face: () => 'background-image:url(/art/story-enemies/e100.webp);background-size:230%;background-position:42% 6%;', cls: 'shuten' },
  // 主人公は顔を出さない（名前の札と暗い吹き出しだけ）
  you: { name: 'あなた', face: () => '', cls: 'you' },
  // 操作の説明（カッコ書き）：顔も名前もない控えめな札
  system: { name: '', face: () => '', cls: 'system' },
};
/** 案内役の絵（ユズハのちびキャラを尻尾・刀まで入るように切り出したもの。tools/art/guide.mjs。表情差分はまだ無いので face は同じ絵。驚きのときだけ少し跳ねる） */
export const guideFaceStyle = (_f: Face = 'normal') => 'background-image:url(/art/guide/yuzuha.webp);background-size:contain;background-position:center bottom;';

let root: HTMLElement | null = null;
let active: { cancel: () => void } | null = null;
/** 今の台詞で触ってよい要素（操作を待つときだけ。それ以外は覆いが全部受け止める） */
let allowed: Element[] = [];

/**
 * 案内の間は、触ってよい所（示す所・allow・吹き出し）以外への操作を止める。
 * 盤の入力は pointer 系、ボタンは click なので、どちらも捕捉の段階で止める。キーの近道も止める
 */
const BLOCK_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'click', 'dblclick', 'contextmenu'];
function guard(e: Event) {
  if (!active) return;
  const t = e.target as Node | null;
  if (t && root?.contains(t)) return; // 吹き出し・覆い（台詞を進める・飛ばす）
  if (t && allowed.some((el) => el.contains(t))) return;
  // 案内の上に開いた小窓（連携のおすすめ・ログインボーナスなど）は押せる（押せないと閉じられず進めなくなる）
  if (t instanceof Element && t.closest('.modal-wrap')) return;
  e.preventDefault();
  e.stopImmediatePropagation();
}
function guardKey(e: KeyboardEvent) {
  if (!active) return;
  if ((e.target as Node | null) && root?.contains(e.target as Node)) return;
  if (document.querySelector('.modal-wrap')) return;
  e.preventDefault();
  e.stopImmediatePropagation();
}
let guarding = false;
function installGuard() {
  if (guarding) return;
  guarding = true;
  for (const ev of BLOCK_EVENTS) window.addEventListener(ev, guard, { capture: true, passive: false });
  window.addEventListener('keydown', guardKey, { capture: true });
}
/** 今、案内を出しているか（ほかの案内を重ねない） */
export const coaching = () => !!active;
/** 攻撃・奥義などの演出中は案内（暗幕と吹き出し）を一時的に消す */
export function quietCoach(on: boolean) {
  root?.classList.toggle('quiet', on);
}

function resolveEl(t: Target | string | (() => Element | null)): Element | null {
  if (!t) return null;
  if (typeof t === 'string') return [...document.querySelectorAll<HTMLElement>(t)].find((x) => x.offsetParent !== null && x.getBoundingClientRect().width > 0) ?? null;
  if (typeof t === 'function') {
    const v = t();
    return v instanceof Element ? v : null;
  }
  return t;
}

function resolveRect(t: Target): DOMRect | null {
  if (!t) return null;
  let el: Element | DOMRect | null = null;
  // 同じセレクタがPC・スマホの両方にあるので、見えているものを選ぶ
  if (typeof t === 'string') el = [...document.querySelectorAll<HTMLElement>(t)].find((x) => x.offsetParent !== null && x.getBoundingClientRect().width > 0) ?? null;
  else if (typeof t === 'function') el = t();
  else el = t;
  if (!el) return null;
  if (el instanceof DOMRect) return el;
  const r = (el as HTMLElement).getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return r;
}

function ensureRoot(): HTMLElement {
  if (root && root.isConnected) return root;
  root = document.createElement('div');
  root.className = 'coach';
  root.hidden = true;
  root.innerHTML = `<div class="coach-dim"></div><div class="coach-spot" hidden></div><div class="coach-also"></div>
    <div class="coach-bubble" role="dialog" aria-live="polite"><span class="coach-face"></span>
      <div class="coach-body"><b class="coach-name">${GUIDE_NAME}</b><p class="coach-text"></p><small class="coach-hint"></small></div></div>`;
  document.body.appendChild(root);
  return root;
}

/** 台詞を順に出す。最後まで進むと true、途中で閉じられた（画面を離れたなど）ら false で解決する。案内を飛ばすボタンは置かない（飛ばすと一本道の案内が不安定になる） */
export function say(lines: Line[]): Promise<boolean> {
  active?.cancel();
  installGuard();
  const el = ensureRoot();
  const dim = el.querySelector<HTMLElement>('.coach-dim')!;
  const spot = el.querySelector<HTMLElement>('.coach-spot')!;
  const bubble = el.querySelector<HTMLElement>('.coach-bubble')!;
  const face = el.querySelector<HTMLElement>('.coach-face')!;
  const text = el.querySelector<HTMLElement>('.coach-text')!;
  const hint = el.querySelector<HTMLElement>('.coach-hint')!;
  return new Promise((done) => {
    let i = -1;
    let raf = 0;
    let shownAt = 0;
    const finish = (ok: boolean) => {
      cancelAnimationFrame(raf);
      allowed = [];
      dim.onclick = null;
      bubble.onclick = null;
      el.hidden = true;
      el.classList.remove('waiting');
      active = null;
      done(ok);
    };
    active = { cancel: () => finish(false) };
    const alsoBox = el.querySelector<HTMLElement>('.coach-also')!;
    /** 複数の所を光らせるとき：覆いに穴を開け（clip-path）、穴ごとに光の枠を置く */
    const placeAlso = (main: DOMRect | null, extra: DOMRect[]) => {
      const multi = !!main && extra.length > 0;
      el.classList.toggle('multi', multi);
      if (!multi) {
        dim.style.clipPath = '';
        alsoBox.innerHTML = '';
        return;
      }
      const pad = 6;
      const holes = [main!, ...extra].map((r) => `M${r.left - pad} ${r.top - pad}h${r.width + pad * 2}v${r.height + pad * 2}h${-(r.width + pad * 2)}Z`).join(' ');
      dim.style.clipPath = `path(evenodd, "M0 0H${innerWidth}V${innerHeight}H0Z ${holes}")`;
      alsoBox.innerHTML = extra.map((r) => `<i class="coach-ring" style="left:${r.left - pad}px;top:${r.top - pad}px;width:${r.width + pad * 2}px;height:${r.height + pad * 2}px"></i>`).join('');
    };
    const place = () => {
      const line = lines[i];
      const r = resolveRect(line.target ?? null);
      const extra = (line.also ?? []).map((t) => resolveRect(t)).filter((x): x is DOMRect => !!x);
      placeAlso(r, extra);
      if (r) {
        const pad = 6;
        spot.hidden = false;
        spot.style.left = `${r.left - pad}px`;
        spot.style.top = `${r.top - pad}px`;
        spot.style.width = `${r.width + pad * 2}px`;
        spot.style.height = `${r.height + pad * 2}px`;
        el.classList.add('spotlit');
        // 吹き出しは示す所と重ならない側（上半分なら下、下半分なら上）
        // 光らせる所が上下の両方にあるときは、間（画面の中ほど）に出す
        const mid = (x: DOMRect) => x.top + x.height / 2 < innerHeight / 2;
        const both = extra.some((x) => mid(x) !== mid(r));
        const below = mid(r);
        bubble.classList.toggle('at-mid', both);
        bubble.classList.toggle('at-bottom', !both && below);
        bubble.classList.toggle('at-top', !both && !below);
      } else {
        spot.hidden = true;
        el.classList.remove('spotlit');
        bubble.classList.remove('at-top', 'at-mid');
        bubble.classList.add('at-bottom');
      }
    };
    const tick = () => {
      const line = lines[i];
      if (!line) return;
      place();
      // 操作を待つ台詞：示す所と allow だけ触れる（画面が描き直されても追いかける）
      allowed = line.wait
        ? [resolveEl(line.target ?? null), ...(line.allow ?? []).map((a) => resolveEl(a))].filter((x): x is Element => !!x)
        : [];
      if (line.wait && line.wait()) return next();
      raf = requestAnimationFrame(tick);
    };
    const next = () => {
      cancelAnimationFrame(raf);
      i++;
      if (i >= lines.length) return finish(true);
      const line = lines[i];
      const sp = SPEAKERS[line.who ?? 'yuzuha'];
      face.setAttribute('style', sp.face());
      face.dataset.face = line.face ?? 'normal';
      el.dataset.who = line.who ?? 'yuzuha';
      el.querySelector<HTMLElement>('.coach-name')!.textContent = sp.name;
      text.innerHTML = esc(line.text).replace(/「(.+?)」/g, '<em>「$1」</em>');
      hint.textContent = line.wait ? (line.hint ?? '') : '画面を押して次へ';
      hint.hidden = !hint.textContent;
      el.classList.toggle('waiting', !!line.wait);
      bubble.classList.remove('pop');
      void bubble.offsetWidth;
      bubble.classList.add('pop');
      shownAt = performance.now();
      el.hidden = false;
      tick();
    };
    // 台詞だけのときは、覆い・吹き出しのどこを押しても次へ（読み飛ばし防止に少し待つ）
    const advance = () => {
      if (lines[i]?.wait) return;
      if (performance.now() - shownAt < 350) return;
      next();
    };
    dim.onclick = advance;
    bubble.onclick = (e) => {
      advance();
    };
    next();
  });
}

/** 出している案内を閉じる（画面を離れたときなど） */
export function closeCoach() {
  active?.cancel();
}
